import React, { useEffect, useState, useMemo } from "react";
import { useSession } from "../context/SessionContext";
import { useSpin } from "../context/SpinContext";
import styles from './SpinCount.module.css';

const SpinCount = () => {
  const { session } = useSession();
  const { state: { spins, spinsFetched }, fetchSpins } = useSpin();
  const [loading, setLoading] = useState(false);
  const [lookbackSpins, setLookbackSpins] = useState(() => {
    // Load saved value from localStorage or default to 100
    return parseInt(localStorage.getItem('spinCountLookback')) || 100;
  });
  const [inputValue, setInputValue] = useState(lookbackSpins);

  useEffect(() => {
    if (session?._id && !loading && !spinsFetched) {
      setLoading(true);
      fetchSpins(session._id)
        .catch(error => {
          console.error("Error fetching spins:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [session?._id, loading, spinsFetched, fetchSpins]);

  const handleSave = () => {
    const newValue = parseInt(inputValue);
    if (isNaN(newValue) || newValue < 1) {
      alert('Please enter a valid number greater than 0');
      return;
    }
    setLookbackSpins(newValue);
    localStorage.setItem('spinCountLookback', newValue.toString());
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  // Calculate counts and percentages for the last X spins
  const spinStats = useMemo(() => {
    if (!spins.length) return { counts: {}, totalSpins: 0 };

    // Get the last X spins
    const recentSpins = spins.slice(-lookbackSpins);
    const totalSpins = recentSpins.length;

    // Initialize counts for all possible spin values
    const counts = {
      '0': 0,
      '00': 0,
      ...Object.fromEntries(Array.from({ length: 36 }, (_, i) => [(i + 1).toString(), 0]))
    };

    // Count occurrences
    recentSpins.forEach(spin => {
      if (counts.hasOwnProperty(spin.spin_value)) {
        counts[spin.spin_value]++;
      }
    });

    return { counts, totalSpins };
  }, [spins, lookbackSpins]);

  const getColor = (spinValue) => {
    const redNumbers = ['1', '3', '5', '7', '9', '12', '14', '16', '18', '19', '21', '23', '25', '27', '30', '32', '34', '36'];
    const blackNumbers = ['2', '4', '6', '8', '10', '11', '13', '15', '17', '20', '22', '24', '26', '28', '29', '31', '33', '35'];
    if (spinValue === '0' || spinValue === '00') return 'green';
    if (redNumbers.includes(spinValue)) return 'red';
    if (blackNumbers.includes(spinValue)) return 'black';
    return '';
  };

  const renderRouletteGrid = () => {
    const numbers = Array.from({ length: 36 }, (_, i) => i + 1);
    const rows = [];
    
    for (let i = 0; i < numbers.length; i += 4) {
      const rowNumbers = numbers.slice(i, i + 4);
      rows.push(
        <React.Fragment key={i}>
          <div className={styles.gridRow}>
            {rowNumbers.map(number => {
              const stringNumber = number.toString();
              const count = spinStats.counts[stringNumber] || 0;
              
              return (
                <div 
                  key={number}
                  className={`${styles.rouletteNumber} ${styles[getColor(stringNumber)]}`}
                >
                  {number}
                  {count > 0 && (
                    <span className={styles.count}>{count}</span>
                  )}
                </div>
              );
            })}
          </div>
          {rowNumbers[rowNumbers.length - 1] === 12 || rowNumbers[rowNumbers.length - 1] === 24 ? 
            <div className={styles.horizontalDivider} /> : null}
        </React.Fragment>
      );
    }
    
    return rows;
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className={styles.spinCountPage}>
      <div className={styles.inputContainer}>
        <div className={styles.inputGroup}>
          <span className={styles.inputLabel}>Look at the last</span>
          <input
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            className={styles.numberInput}
            min="1"
          />
          <span className={styles.inputLabel}>spins</span>
          <button 
            onClick={handleSave}
            className={styles.saveButton}
          >
            Save
          </button>
        </div>
      </div>

      <div className={styles.statsInfo}>
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Showing:</span>
          <span className={styles.statValue}>
            Last {Math.min(lookbackSpins, spins.length)} of {spins.length} spins
          </span>
        </div>
      </div>
      
      <div className={styles.rouletteContainer}>
        <div className={styles.rouletteTable}>
          <div className={styles.zerosRow}>
            {['0', '00'].map(zeroValue => {
              const count = spinStats.counts[zeroValue] || 0;
              
              return (
                <div 
                  key={zeroValue}
                  className={`${styles.rouletteNumber} ${styles.green}`}
                >
                  {zeroValue}
                  {count > 0 && (
                    <span className={styles.count}>{count}</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className={styles.numbersGrid}>
            {renderRouletteGrid()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpinCount;